import React, { useEffect, useState } from 'react';
import { Row, Col, Label } from 'reactstrap';
import Select from 'react-select';
import { AssignmentInput } from './AutoAssignTypes';
import { useSelectStyles } from 'Components/Hooks/useSelectStyles';

interface Props {
	metaDataFields: any[]; // Full metadata
	assignments: Record<string, AssignmentInput>;
	onUpdate: (key: string, assignment: AssignmentInput) => void;
	roles: any[]; // { label, value }
	teams: any[]; // { label, value }
	users: any[]; // { label, value }
	t: (key: string) => string;
}

const DynamicAssignment: React.FC<Props> = ({ metaDataFields, assignments, onUpdate, roles, teams, users, t }) => {
	const customStyles = useSelectStyles();
	const [assignmentFields, setAssignmentFields] = useState<any[]>([]);
	const [assignmentModes, setAssignmentModes] = useState<Record<string, 'users' | 'roles' | 'teams'>>({});

	useEffect(() => {
		const fields = metaDataFields
			.filter((f: any) => {
				const json = f.valuesJson && typeof f.valuesJson === 'string' ? JSON.parse(f.valuesJson) : f.valuesJson;
				return json?.model === 'users';
			})
			.map((f: any) => {
				const json = f.valuesJson && typeof f.valuesJson === 'string' ? JSON.parse(f.valuesJson) : f.valuesJson;
				return {
					key: json.key,
					label: json.label,
					multiple: json.multiple ?? true,
				};
			});

		setAssignmentFields(fields);

		// Initialize assignmentModes if missing
		const defaultModes = fields.reduce((acc, field) => {
			acc[field.key] = assignments[field.key]?.type || 'users';
			return acc;
		}, {} as Record<string, 'users' | 'roles' | 'teams'>);

		setAssignmentModes(defaultModes);
	}, [metaDataFields, assignments]);

	const getOptions = (mode: 'users' | 'roles' | 'teams') => {
		switch (mode) {
			case 'roles': return roles;
			case 'teams': return teams;
			default: return users;
		}
	};

	const handleModeChange = (key: string, mode: 'users' | 'roles' | 'teams') => {
		setAssignmentModes((prev) => ({ ...prev, [key]: mode }));
		onUpdate(key, { type: mode, values: [] });
	};

	const handleValueChange = (key: string, values: any[]) => {
		onUpdate(key, { type: assignmentModes[key], values });
	};

	return (
		<Row className="gy-4">
			{assignmentFields.map((f) => (
				<Col md={6} key={f.key}>
					<Label className="fw-semibold">{f.label}</Label>

					<Select
						className="mb-2"
						value={{ value: assignmentModes[f.key], label: (`${t('properties.auto_assign.by')} ${assignmentModes[f.key]}`) }}
						options={[
							{ value: 'users', label: t('properties.auto_assign.by_user') },
							{ value: 'roles', label: t('properties.auto_assign.by_role') },
							{ value: 'teams', label: t('properties.auto_assign.by_team') },
						]}
						onChange={(mode: any) => handleModeChange(f.key, mode.value)}
						isSearchable={false}
					/>

					<Select
						isMulti
						styles={customStyles}
						placeholder={t('common.filter.search.select')}
						value={assignments[f.key]?.values || []}
						onChange={(vals: any[]) => handleValueChange(f.key, vals)}
						options={getOptions(assignmentModes[f.key])}
					/>
				</Col>
			))}
		</Row>
	);
};

export default DynamicAssignment;
